// Client mirror of functions-common/src/repeat/keys.ts. Keep in sync.

import {
  formatZonedDateKey,
  getMonthBucketKey,
  getQuarterBucketKey,
  getWeekBucketKey,
  zonedTimeToUtc,
} from '../../charts/engine/bucket'
import {
  type Cadence,
  type CadenceKind,
  CadenceNotBuiltError,
} from './types'

export {
  formatZonedDateKey,
  getMonthBucketKey,
  getQuarterBucketKey,
  getWeekBucketKey,
  zonedTimeToUtc,
} from '../../charts/engine/bucket'

export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || !timeZone.trim()) return false
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timeZone.trim() })
    return true
  } catch {
    return false
  }
}

export function resolveWorkspaceTimeZone(raw?: unknown): string {
  return isValidTimeZone(raw) ? (raw as string).trim() : 'UTC'
}

export function extractCadenceKind(cadence: Cadence): CadenceKind {
  return typeof cadence === 'string' ? cadence : cadence.kind
}

export function dayKeyToUtcMs(dayKey: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
    throw new Error(`Invalid dayKey: ${dayKey}`)
  }
  const [y, m, d] = dayKey.split('-').map(Number)
  const utc = Date.UTC(y, m - 1, d)
  const check = new Date(utc)
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() + 1 !== m ||
    check.getUTCDate() !== d
  ) {
    throw new Error(`Invalid calendar dayKey: ${dayKey}`)
  }
  return utc
}

export function utcMsToDayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

export function extractPerShiftId(cadence: Cadence): string {
  if (typeof cadence === 'string' || cadence.kind !== 'PER_SHIFT') {
    throw new Error('PER_SHIFT cadence requires shiftId')
  }
  const shiftId = typeof cadence.shiftId === 'string' ? cadence.shiftId.trim() : ''
  if (!shiftId) {
    throw new Error('PER_SHIFT cadence requires non-empty shiftId')
  }
  return shiftId
}

export function extractEveryNDaysParams(cadence: Cadence): {
  intervalDays: number
  anchorDayKey: string
  anchorMs: number
} {
  if (typeof cadence === 'string' || cadence.kind !== 'EVERY_N_DAYS') {
    throw new Error('EVERY_N_DAYS cadence requires intervalDays and anchorDayKey')
  }
  const { intervalDays, anchorDayKey } = cadence
  if (!Number.isInteger(intervalDays) || intervalDays < 2 || intervalDays > 365) {
    throw new Error(`EVERY_N_DAYS intervalDays must be an integer 2..365, got ${intervalDays}`)
  }
  if (typeof anchorDayKey !== 'string') {
    throw new Error('EVERY_N_DAYS requires anchorDayKey YYYY-MM-DD')
  }
  const anchorMs = dayKeyToUtcMs(anchorDayKey)
  return { intervalDays, anchorDayKey, anchorMs }
}

export function startOfIsoWeekDate(date: Date, timeZone?: string): Date {
  if (!timeZone) {
    const clone = new Date(date)
    const day = clone.getDay()
    const delta = (day + 6) % 7
    clone.setDate(clone.getDate() - delta)
    clone.setHours(0, 0, 0, 0)
    return clone
  }
  const tz = resolveWorkspaceTimeZone(timeZone)
  const dayStr = formatZonedDateKey(date, tz)
  const [y, m, d] = dayStr.split('-').map(Number)
  const targetUtc = new Date(Date.UTC(y, m - 1, d))
  const dayOfWeek = targetUtc.getUTCDay()
  const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek
  targetUtc.setUTCDate(targetUtc.getUTCDate() - (isoDay - 1))
  return zonedTimeToUtc(
    targetUtc.getUTCFullYear(),
    targetUtc.getUTCMonth() + 1,
    targetUtc.getUTCDate(),
    0,
    0,
    0,
    0,
    tz,
  )
}

export function periodKeyFor(
  cadence: Cadence,
  instant: Date | number | string,
  timeZone?: string,
): string {
  const kind = extractCadenceKind(cadence)
  const tz = resolveWorkspaceTimeZone(timeZone)
  const date = instant instanceof Date ? instant : new Date(instant)

  switch (kind) {
    case 'DAILY':
      return formatZonedDateKey(date, tz)
    case 'WEEKLY':
      return getWeekBucketKey(date, tz)
    case 'MONTHLY':
      return getMonthBucketKey(date, tz)
    case 'QUARTERLY':
      return getQuarterBucketKey(date, tz)
    case 'YEARLY':
      return formatZonedDateKey(date, tz).slice(0, 4)
    case 'EVERY_N_DAYS': {
      const { intervalDays, anchorMs } = extractEveryNDaysParams(cadence)
      const dayKey = formatZonedDateKey(date, tz)
      const dayMs = dayKeyToUtcMs(dayKey)
      const diffDays = Math.round((dayMs - anchorMs) / 86400000)
      const offsetDays = Math.floor(diffDays / intervalDays) * intervalDays
      return utcMsToDayKey(anchorMs + offsetDays * 86400000)
    }
    case 'PER_SHIFT': {
      const shiftId = extractPerShiftId(cadence)
      const dayKey = formatZonedDateKey(date, tz)
      return `${dayKey}~${shiftId}`
    }
    case 'ON_DEMAND':
      throw new CadenceNotBuiltError(kind)
    default: {
      const exhaustiveCheck: never = kind
      throw new CadenceNotBuiltError(String(exhaustiveCheck))
    }
  }
}

export function daysForWeekKey(weekKey: string): { monday: string; sunday: string } {
  const [yearRaw, weekRaw] = weekKey.split('-W')
  const year = Number(yearRaw)
  const week = Number(weekRaw)
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const dayOfWeek = jan4.getUTCDay()
  const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek
  const monW1 = new Date(Date.UTC(year, 0, 4 - (isoDay - 1)))
  const monday = new Date(monW1.getTime() + (week - 1) * 7 * 86400000)
  const sunday = new Date(monday.getTime() + 6 * 86400000)
  return {
    monday: monday.toISOString().slice(0, 10),
    sunday: sunday.toISOString().slice(0, 10),
  }
}

export function weekKeyForDayKey(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const dateUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  return getWeekBucketKey(dateUtc, 'UTC')
}

export function workspaceTodayKey(
  cadence: Cadence,
  rawWorkspaceTimezone: unknown,
  now: Date = new Date(),
): string {
  const tz = resolveWorkspaceTimeZone(rawWorkspaceTimezone)
  return periodKeyFor(cadence, now, tz)
}
